import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  Bell,
  Calendar, 
  FileText, 
  MessageSquare, 
  Briefcase,
  UserCheck,
  Mail,
  AlertCircle,
  AtSign,
  Users,
  type LucideIcon
} from "lucide-react";

interface NotificationFilterChipsProps {
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  counts: Record<string, number>;
}

interface FilterConfig {
  value: string;
  icon: LucideIcon;
  label: string;
  colorClass: string;
}

const FILTERS: FilterConfig[] = [
  { value: "all", icon: Bell, label: "All", colorClass: "text-foreground" },
  { value: "mention", icon: AtSign, label: "Mentions", colorClass: "text-purple-600" },
  { value: "collaboration_invite", icon: Users, label: "Invites", colorClass: "text-cyan-600" },
  { value: "interview_scheduled", icon: Calendar, label: "Interviews", colorClass: "text-blue-600" },
  { value: "application_status", icon: FileText, label: "Applications", colorClass: "text-green-600" },
  { value: "message", icon: MessageSquare, label: "Messages", colorClass: "text-purple-600" },
  { value: "application_received", icon: Briefcase, label: "Jobs", colorClass: "text-indigo-600" },
  { value: "candidate_applied", icon: UserCheck, label: "Candidates", colorClass: "text-teal-600" },
  { value: "email", icon: Mail, label: "Emails", colorClass: "text-orange-600" },
  { value: "alert", icon: AlertCircle, label: "Alerts", colorClass: "text-red-600" },
];

export function NotificationFilterChips({
  activeFilter,
  onFilterChange,
  counts,
}: NotificationFilterChipsProps) {
  return (
    <div className="relative">
      {/* Gradient fade edges for scroll indication */}
      <div className="absolute left-0 top-0 bottom-2 w-4 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-2 w-4 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
      
      <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide px-4 border-b">
        {FILTERS.map((filter) => {
          const Icon = filter.icon;
          const count = counts[filter.value] || 0;
          const isActive = activeFilter === filter.value;
          
          // Don't show filters with 0 count (except "all")
          if (filter.value !== "all" && count === 0) return null;
          
          return (
            <Badge
              key={filter.value}
              variant={isActive ? "default" : "outline"}
              className={cn(
                "h-9 px-3 cursor-pointer whitespace-nowrap transition-all border",
                "hover:scale-105 active:scale-95",
                isActive 
                  ? "shadow-sm" 
                  : "hover:bg-muted"
              )}
              onClick={() => onFilterChange(filter.value)}
            >
              <Icon className={cn(
                "h-3.5 w-3.5",
                isActive ? "text-primary-foreground" : filter.colorClass
              )} />
              <span className={cn(
                "ml-1.5 text-xs font-medium",
                isActive ? "text-primary-foreground" : "text-foreground"
              )}>
                {filter.label}
              </span>
              {count > 0 && filter.value !== "all" && (
                <span className={cn(
                  "ml-1 text-[10px] px-1.5 py-0.5 rounded-full",
                  isActive 
                    ? "bg-primary-foreground/20 text-primary-foreground" 
                    : "bg-muted text-muted-foreground"
                )}>
                  {count}
                </span>
              )}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
